/**
 * Base API client. Wraps fetch with:
 *   - JWT bearer injection from the auth store
 *   - JSON serialization
 *   - Typed error responses from the backend's error handler shape
 *   - Automatic logout on 401
 *
 * All endpoint modules (api/auth.ts, api/videos.ts, etc.) use this
 * client instead of calling fetch directly.
 */
import { useAuthStore } from "@/stores/authStore.ts";
import { refusalOf, type Refusal } from "./refusal.ts";

const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ??
  "/api";

export interface ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  requestId?: string;
}

export class ApiErrorImpl extends Error implements ApiError {
  status: number;
  code: string;
  details?: unknown;
  requestId?: string;

  constructor(params: {
    message: string;
    status: number;
    code: string;
    details?: unknown;
    requestId?: string;
  }) {
    super(params.message);
    this.name = "ApiError";
    this.status = params.status;
    this.code = params.code;
    this.details = params.details;
    this.requestId = params.requestId;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

// Token refresh: if the token expires within 5 minutes, refresh it before the request.
let refreshPromise: Promise<void> | null = null;

export async function ensureFreshToken(): Promise<void> {
  const { token, expiresAt, setAuth, clearAuth } = useAuthStore.getState();
  if (!token || !expiresAt) return;

  const fiveMinutes = 5 * 60 * 1000;
  if (Date.now() + fiveMinutes < expiresAt) return; // still fresh

  // Coalesce concurrent refresh attempts
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "omit",
      });
      if (!res.ok) {
        clearAuth();
        return;
      }
      const data = await res.json();
      if (data.token && data.user) {
        setAuth({ user: data.user, token: data.token, expiresAt: data.expiresAt });
      }
    } catch {
      // Refresh failed silently — the original request will proceed
      // and get a 401 which triggers auto-logout
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function apiRequest<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  // Proactively refresh token if it's about to expire
  if (!path.includes("/auth/refresh") && !path.includes("/auth/login") && !path.includes("/auth/signup")) {
    await ensureFreshToken();
  }

  const token = useAuthStore.getState().token;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...options.headers,
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? "GET",
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
    // We use bearer tokens (not cookies), so credentials are not needed.
    // Keeping this 'omit' protects against CSRF if cookies are ever added later.
    credentials: "omit",
  });

  // Non-JSON responses (shouldn't happen but guard)
  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");

  if (!response.ok) {
    // (B111; audit N3) the body's code, message and details, whichever of its shapes it has (api/refusal.ts)
    let r: Refusal = refusalOf(response.status, null);
    if (isJson) {
      try { r = refusalOf(response.status, await response.json()); }
      catch { /* catch-reason: a body that isn't JSON keeps the status's own words */ }
    }
    const { code, message, details, requestId } = r;

    // Auto-logout on 401
    if (response.status === 401) {
      useAuthStore.getState().clearAuth();
    }

    throw new ApiErrorImpl({
      status: response.status,
      code,
      message,
      details,
      requestId,
    });
  }

  if (!isJson) {
    // No body; return undefined cast to TResponse (caller should type as void)
    return undefined as TResponse;
  }

  return (await response.json()) as TResponse;
}

/** Convenience helper for GET requests. */
export const apiGet = <T>(path: string, signal?: AbortSignal) =>
  apiRequest<T>(path, { method: "GET", signal });

/** Convenience helper for POST requests. */
export const apiPost = <T>(path: string, body?: unknown) =>
  apiRequest<T>(path, { method: "POST", body });

/** Convenience helper for PATCH requests. */
export const apiPatch = <T>(path: string, body?: unknown) =>
  apiRequest<T>(path, { method: "PATCH", body });

/** Convenience helper for PUT requests. */
export const apiPut = <T>(path: string, body?: unknown) =>
  apiRequest<T>(path, { method: "PUT", body });

/** Convenience helper for DELETE requests. */
export const apiDelete = <T>(path: string) =>
  apiRequest<T>(path, { method: "DELETE" });

export { API_BASE_URL };
